import { MONTHS } from '@/common/constants';
import { useSidebar } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import React, { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IStewardshipTableProps } from '../models/IStewardshipTable';

export const StewardshipTable: FC<IStewardshipTableProps> = ({
  columns,
  headerRows,
  monthsData,
  onColumnClick,
  className,
}) => {
  const { t } = useTranslation();
  const { open } = useSidebar();
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);

  const formatNumber = (num: number): string => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  useEffect(() => {
    const headerElement = headerScrollRef.current;
    const bodyElement = bodyScrollRef.current;

    if (!headerElement || !bodyElement) return;

    const handleBodyScroll = () => {
      if (headerElement.scrollLeft !== bodyElement.scrollLeft) {
        headerElement.scrollLeft = bodyElement.scrollLeft;
      }
    };

    const handleHeaderScroll = () => {
      if (bodyElement.scrollLeft !== headerElement.scrollLeft) {
        bodyElement.scrollLeft = headerElement.scrollLeft;
      }
    };

    bodyElement.addEventListener('scroll', handleBodyScroll);
    headerElement.addEventListener('scroll', handleHeaderScroll);

    return () => {
      bodyElement.removeEventListener('scroll', handleBodyScroll);
      headerElement.removeEventListener('scroll', handleHeaderScroll);
    };
  }, []);

  return (
    <div
      className={cn(
        'h-full w-full rounded-10 border bg-white overflow-hidden flex flex-col',
        className,
      )}
    >
      <div
        ref={headerScrollRef}
        className={cn(
          'flex-shrink-0 border-b overflow-x-auto overflow-y-hidden scrollbar-hide',
          open ? 'w-[calc(100vw-320px)]' : 'w-[calc(100vw-150px)]',
        )}
      >
        <table
          className="caption-bottom text-sm border-collapse"
          style={{ width: 'max-content', minWidth: '100%' }}
        >
          <thead className="bg-gray-100">
            <tr className="border-b bg-gray-100 hover:bg-gray-100">
              <th className="h-10 w-[200px] text-[12px] font-medium text-black/60 border-b py-3 bg-gray-100 text-left align-middle px-4"></th>
              {columns.map((column) => (
                <th
                  key={column.id}
                  className="h-10 w-[140px] text-[12px] text-center font-medium text-black/60 border-b py-3 px-1 bg-gray-100 align-middle cursor-pointer"
                  onClick={() => onColumnClick?.(column)}
                >
                  <div className="w-full flex justify-center">
                    <p className="text-sm font-medium text-dark-blue text-center hover:text-cyan-default cursor-pointer duration-300">
                      {column.name}
                    </p>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {headerRows.map((row, index) => (
              <tr
                key={index}
                className={cn(
                  'border-b',
                  index === headerRows.length - 1
                    ? 'bg-gray-100 hover:bg-gray-100'
                    : 'bg-white',
                )}
              >
                <td
                  className={cn(
                    'h-[52px] w-[200px] align-middle px-4',
                    index === 0 && 'border-t',
                    index === headerRows.length - 1 && 'bg-gray-100 border-t',
                  )}
                >
                  {row.label}
                </td>
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={cn(
                      'h-[52px] w-[140px] text-center px-1 align-middle',
                      index === headerRows.length - 1
                        ? 'text-sm font-medium text-dark-blue bg-gray-100'
                        : 'bg-white',
                    )}
                  >
                    {row.getValue(column)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Scrollable Body Section */}
      <div
        ref={bodyScrollRef}
        className={cn(
          'flex-1 overflow-auto',
          open ? 'w-[calc(100vw-320px)]' : 'w-[calc(100vw-150px)]',
        )}
      >
        <table
          className="caption-bottom text-sm border-collapse"
          style={{ width: 'max-content', minWidth: '100%' }}
        >
          <thead className="invisible">
            <tr>
              <th className="h-0 w-[200px] px-4"></th>
              {columns.map((column) => (
                <th key={column.id} className="h-0 w-[140px] px-1"></th>
              ))}
            </tr>
          </thead>
          <tbody>
            {monthsData.map((monthData) => (
              <React.Fragment key={monthData.month}>
                <tr className="border-b bg-gray-50 hover:bg-gray-50">
                  <td
                    colSpan={columns.length + 1}
                    className="h-[42px] border-t font-medium align-middle px-4"
                  >
                    {t(
                      `${MONTHS.find((item) => item.value === monthData.month)?.value}`,
                    )}
                  </td>
                </tr>
                {monthData.actions.map((action, index) => (
                  <React.Fragment
                    key={`${monthData.month}-${action.action_name}`}
                  >
                    <tr className="border-b bg-gray-100 hover:bg-gray-100">
                      <td
                        className="h-[44px] w-[200px] py-1 align-middle px-4 font-medium"
                        colSpan={columns.length + 1}
                      >
                        {`${index + 1}. ${action.action_name}`}{' '}
                        {monthData.month === 'APRIL' &&
                          action.action_name === 'Chigit ekish' ? (
                          <span className="font-semibold">({t('total')})</span>
                        ) : (
                          ''
                        )}
                      </td>
                    </tr>

                    <tr className="border-b hover:bg-light border-gray-100">
                      <td className="h-[44px] w-[200px] py-1 align-middle px-8 text-gray-600">
                        {t('responsible.users.count')}
                      </td>
                      {columns.map((column) => {
                        const columnData = action.columnData[column.id] || {
                          responsible_users_count: 0,
                          completed_actions_count: 0,
                          pending_actions_count: 0,
                        };
                        return (
                          <td
                            key={column.id}
                            className="h-[44px] w-[140px] text-center px-1 align-middle"
                          >
                            {formatNumber(columnData.responsible_users_count)}
                          </td>
                        );
                      })}
                    </tr>

                    <tr className="border-b hover:bg-light border-gray-100">
                      <td className="h-[44px] w-[200px] py-1 align-middle px-8 text-gray-600">
                        {t('checked.short')}
                      </td>
                      {columns.map((column) => {
                        const columnData = action.columnData[column.id] || {
                          responsible_users_count: 0,
                          completed_actions_count: 0,
                          pending_actions_count: 0,
                        };
                        return (
                          <td
                            key={column.id}
                            className="h-[44px] w-[140px] text-center px-1 align-middle"
                          >
                            {formatNumber(columnData.completed_actions_count)}
                          </td>
                        );
                      })}
                    </tr>

                    <tr className="border-b hover:bg-light border-gray-100">
                      <td className="h-[44px] w-[200px] py-1 align-middle px-8 text-gray-600">
                        {t('unchecked')}
                      </td>
                      {columns.map((column) => {
                        const columnData = action.columnData[column.id] || {
                          responsible_users_count: 0,
                          completed_actions_count: 0,
                          pending_actions_count: 0,
                        };
                        return (
                          <td
                            key={column.id}
                            className="h-[44px] w-[140px] text-center px-1 align-middle"
                          >
                            {formatNumber(columnData.pending_actions_count)}
                          </td>
                        );
                      })}
                    </tr>
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
